/**
 * Environment configuration for API integration
 * 
 * Controls whether to use real backend APIs or MSW mocks, plus base URL configuration.
 * Set VITE_USE_BACKEND=true to switch from mock to real API calls.
 */

// Trim potential whitespace; fix stray leading space in default.
// NOTE: Do NOT include path segments like /api/v1 here because individual service
// clients already prefix their own versioned paths (e.g. `/api/v1/...`). If you add
// /api/v1 here you will end up with a duplicated path: /api/v1/api/v1/...
// Provided production / dev container apps base URL (no trailing slash):
// https://ca-edp-dev-074.victoriouspond-91d24c7a.eastus.azurecontainerapps.io
// Raw value may (incorrectly) include /api/v1. We strip it to avoid duplicated
// paths when services append versioned prefixes. (.env.local currently overrides
// defaults, so users who followed earlier docs with /api/v1 will still work.)
const RAW_API_BASE = (
    import.meta.env.VITE_API_BASE_URL ||
    '__VITE_API_BASE_URL__' ||
    'https://ca-edp-dev-074.victoriouspond-91d24c7a.eastus.azurecontainerapps.io'
).trim();

// Normalize: remove trailing slash
let normalizedBase = RAW_API_BASE.replace(/\/$/, '');
if (/\/api\/v1$/i.test(normalizedBase)) {
    normalizedBase = normalizedBase.replace(/\/api\/v1$/i, '');
}

export const API_BASE_URL = normalizedBase;

// Convert HTTP(S) API URL to WebSocket URL for real-time AI services
export const WS_BASE_URL = API_BASE_URL.replace(/^https?:/, API_BASE_URL.startsWith('https:') ? 'wss:' : 'ws:');

// Central API version path (single source of truth)
export const API_VERSION = '/api/v1';

// Helper to build a fully versioned API path (ensures leading slash after version)
export function apiPath(relative: string): string {
    // Normalize any backslashes to forward slashes (Windows compatibility)
    const normalizedRelative = relative.replace(/\\/g, '/');
    const rel = normalizedRelative.startsWith('/') ? normalizedRelative : `/${normalizedRelative}`;
    return `${API_VERSION}${rel}`;
}

// Backend usage flag (feature toggle). Controlled via Vite env var VITE_USE_BACKEND=true
// In development: uses .env.local value directly  
// In production: defaults to true (backend always enabled in deployed environments)
export const USE_BACKEND = import.meta.env.VITE_USE_BACKEND === 'true' || import.meta.env.PROD;

/**
 * Generate correlation ID for request tracing
 */
export function generateCorrelationId(): string {
    return crypto.randomUUID();
}

/**
 * Dev mode logging flag
 */
export const DEV_MODE = import.meta.env.DEV;

// Explicit dev auth bypass flag. Only active in DEV when the env var exists AND is exactly "true" (case-insensitive).
// If the variable is absent or any other value (including blank), bypass is disabled.
const rawBypass = (import.meta.env.VITE_BYPASS_AUTH ?? undefined);
export const BYPASS_AUTH = Boolean(import.meta.env.DEV && rawBypass && rawBypass.toLowerCase() === 'true');

// Optional dev email used only when BYPASS_AUTH is true. Falls back to a deterministic placeholder.
export const DEV_EMAIL = (import.meta.env.VITE_DEV_EMAIL || 'dev@example.com').trim();

/**
 * Azure AD Authentication Configuration (User Login Only)
 */
export const AZURE_AD_CONFIG = {
    // Azure AD tenant (will be injected at runtime in container environments)
    tenantId: import.meta.env.VITE_AZURE_TENANT_ID || '__VITE_AZURE_TENANT_ID__',

    // Azure AD application (client) ID from your app registration
    clientId: import.meta.env.VITE_AZURE_CLIENT_ID || '__VITE_AZURE_CLIENT_ID__',

    // Redirect URI after successful login (should match your app registration)
    redirectUri: import.meta.env.VITE_AZURE_REDIRECT_URI || '__VITE_AZURE_REDIRECT_URI__' || window.location.origin,

    // Post logout redirect URI
    postLogoutRedirectUri: import.meta.env.VITE_AZURE_POST_LOGOUT_REDIRECT_URI || '__VITE_AZURE_POST_LOGOUT_REDIRECT_URI__' || window.location.origin,

    // Azure AD authority (common or specific tenant)
    authority: import.meta.env.VITE_AZURE_AUTHORITY || '__VITE_AZURE_AUTHORITY__' || `https://login.microsoftonline.com/${import.meta.env.VITE_AZURE_TENANT_ID || '__VITE_AZURE_TENANT_ID__'}`,

    // Scopes requested during login (user profile access only)
    scopes: (import.meta.env.VITE_AZURE_SCOPES || '__VITE_AZURE_SCOPES__' || 'openid,profile,User.Read')
        .split(',')
        .map((scope: string) => scope.trim()),

    // API scopes for backend access (needed for admin endpoints)
    apiScopes: (import.meta.env.VITE_AZURE_API_SCOPES || '')
        .split(',')
        .map((scope: string) => scope.trim())
        .filter(scope => scope.length > 0)
} as const;

/** Session timeout in minutes (read from VITE_SESSION_TIMEOUT_MINUTES) */
const rawTimeout = (import.meta.env.VITE_SESSION_TIMEOUT_MINUTES || '__VITE_SESSION_TIMEOUT_MINUTES__').trim();
export const SESSION_TIMEOUT_MS = Number(
    rawTimeout && rawTimeout !== '__VITE_SESSION_TIMEOUT_MINUTES__' ? rawTimeout : 30
) * 60 * 1000;

/**
 * Admin configuration
 */
// Admin Panel Configuration
export const ADMIN_CONFIG = {
    // AD Group name for admin access
    adminGroupName: (() => {
        const raw = (import.meta.env.VITE_ADMIN_GROUP_NAME || '__VITE_ADMIN_GROUP_NAME__').trim();
        return raw && raw !== '__VITE_ADMIN_GROUP_NAME__' ? raw : 'edp-data-doctor-admins';
    })()
};